import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import Loader from '../../components/UI/Loader/loader';
import styles from './Register.module.css';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuthService, useSpecialtyService } from '../../hooks';
import { ErrorObject } from '../../types/types';
import { loaderActions } from '../../store/loader';
import { Validator, SystemRole, Roles } from '../../../utils';
import { Specialty, SpecialtyCode } from '../../../domain/entities';
import DoctorExtraFields from './components/DoctorExtraFields';

type MedicalCenter = 'sauces' | 'duran' | undefined;

const Register = () => {
  const AuthService = useAuthService();
  const SpecialtyService = useSpecialtyService();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<SystemRole>('secretary');
  const [specialty, setSpecialty] = useState<Specialty>();
  const [medicalCenter, setMedicalCenter] = useState<MedicalCenter>('sauces');

  const [specialties, setSpecialties] = useState<Specialty[]>([]);

  const [emailError, setEmailError] = useState<ErrorObject>({
    hasError: false,
    message: ''
  });
  const [passwordError, setPasswordError] = useState<ErrorObject>({
    hasError: false,
    message: ''
  });

  const isLoading = (state: boolean) => {
    dispatch(loaderActions.setLoading(state));
  };

  useEffect(() => {
    isLoading(true);
    SpecialtyService.listSpecialties()
      .then((data) => {
        setSpecialties(data);
        setSpecialty(data[0]);
      })
      .catch((e) => console.log(e))
      .finally(() => isLoading(false));
  }, []);

  const isValid = () => {
    const emailErrorData = Validator(email, ['required']);
    setEmailError(emailErrorData);
    const passwordErrorData = Validator(password, ['required']);
    setPasswordError(passwordErrorData);
    return !emailErrorData.hasError && !passwordErrorData.hasError;
  };

  const signup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValid()) return;

    isLoading(true);
    AuthService.signup({ email, password, name, role, specialty, medicalCenter })
      .then(() => navigate('/login'))
      .catch((e) => console.log(e))
      .finally(() => isLoading(false));
  };

  const changeRole = (event: SelectChangeEvent<SystemRole>) => {
    setRole(event.target.value as SystemRole);
  };

  const changeSpecialty = (event: SelectChangeEvent<SpecialtyCode>) => {
    isLoading(true);
    SpecialtyService.getOne(event.target.value as SpecialtyCode)
      .then((data) => setSpecialty(data))
      .catch((e) => console.log(e))
      .finally(() => isLoading(false));
  };

  const changeMedicalCenter = (event: SelectChangeEvent<MedicalCenter>) => {
    setMedicalCenter(event.target.value as MedicalCenter);
  };

  const goToLogin = () => {
    navigate('/login');
  };

  return (
    <Box className={styles.container}>
      <form onSubmit={signup} autoComplete='off'>
        <Card className={styles.card}>
          <TextField
            id='email'
            label='Email'
            variant='outlined'
            margin='normal'
            color='primary'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError.hasError}
            helperText={emailError.message}
          />
          <TextField
            id='password'
            type='password'
            label='Password'
            variant='outlined'
            margin='normal'
            color='primary'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError.hasError}
            helperText={passwordError.message}
          />
          <TextField
            id='name'
            type='name'
            label='Nombre'
            variant='outlined'
            margin='normal'
            color='primary'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormControl margin='normal' fullWidth>
            <InputLabel id='role-label'>Rol</InputLabel>
            <Select labelId='role' id='role-select' value={role} label='Rol' onChange={changeRole}>
              <MenuItem value={Roles.secretary}>Secretario/a</MenuItem>
              <MenuItem value={Roles.doctor}>Doctor/a</MenuItem>
            </Select>
          </FormControl>
          {role === Roles.doctor && (
            <DoctorExtraFields
              specialties={specialties}
              specialty={specialty}
              medicalCenter={medicalCenter}
              onSpecialtyChanged={changeSpecialty}
              onMedicalCenterChanged={changeMedicalCenter}
            />
          )}
          <Button type='submit' variant='contained' color='primary' sx={{ marginTop: '10px' }}>
            Register
          </Button>
          <Box className={styles.divider} sx={{ marginY: '4px' }}>
            <span className={styles.dividerLabel}>or</span>
          </Box>
          <Button type='button' variant='outlined' color='primary' onClick={goToLogin}>
            Login
          </Button>
        </Card>
      </form>
      <Loader />
    </Box>
  );
};

export default Register;
